import React, { useMemo } from 'react';
import {
    DollarSign,
    TrendingUp,
    PieChart,
    AlertTriangle
} from 'lucide-react';
import { useFunnelBuilder } from '../../contexts/FunnelBuilderContext';
import { calculateBudgetByStage, calculateBudgetByPlatform, FUNNEL_STAGES } from '../../utils/funnelHelpers';

/**
 * BudgetOverview Component
 * Budget visualization panel showing allocation by stage and platform
 * @param {boolean} collapsed - Show collapsed view
 * @param {boolean} embedded - When true, renders without container (for embedding in other panels)
 */
const BudgetOverview = ({ collapsed = false, embedded = false }) => {
    const { nodes, totalBudget, activePlatform } = useFunnelBuilder();

    const budgetData = useMemo(() => {
        const byStage = calculateBudgetByStage(nodes);
        const byPlatform = calculateBudgetByPlatform(nodes);

        // Calculate percentages
        const stagePercentages = {};
        Object.entries(byStage).forEach(([stage, amount]) => {
            stagePercentages[stage] = totalBudget > 0
                ? Math.round((amount / totalBudget) * 100)
                : 0;
        });

        // Budget recommendations (rough heuristics)
        const warnings = [];
        if (stagePercentages.TOFU < 20 && totalBudget > 0) {
            warnings.push('Consider allocating more to TOFU for awareness');
        }
        if (stagePercentages.BOFU > 60 && totalBudget > 0) {
            warnings.push('High BOFU allocation - ensure sufficient top-of-funnel traffic');
        }
        if (stagePercentages.MOFU === 0 && stagePercentages.TOFU > 0 && stagePercentages.BOFU > 0) {
            warnings.push('No MOFU campaigns - consider adding consideration stage');
        }

        return {
            byStage,
            byPlatform,
            stagePercentages,
            warnings
        };
    }, [nodes, totalBudget]);

    const formatBudget = (amount) => {
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    if (collapsed) {
        return (
            <div style={{
                padding: 'var(--space-3)',
                background: 'var(--color-surface)',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <DollarSign size={16} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontWeight: 600 }}>{formatBudget(totalBudget)}/day</span>
                </div>
                {budgetData.warnings.length > 0 && (
                    <AlertTriangle size={16} style={{ color: 'var(--color-warning)' }} />
                )}
            </div>
        );
    }

    // Content to render (reused for both embedded and standalone modes)
    const content = (
        <>
            {/* Total Budget */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg-tertiary)'
            }}>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-1)'
                }}>
                    Total Daily Budget
                </div>
                <div style={{
                    fontSize: 'var(--font-size-2xl)',
                    fontWeight: 700,
                    color: 'var(--color-primary)'
                }}>
                    {formatBudget(totalBudget)}
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    marginTop: 'var(--space-1)'
                }}>
                    ~{formatBudget(totalBudget * 30)}/month
                </div>
            </div>

            {/* Budget by Stage */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                flex: 1,
                overflowY: 'auto'
            }}>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-3)'
                }}>
                    By Funnel Stage
                </div>

                {/* Visual Bar */}
                <div style={{
                    display: 'flex',
                    height: '12px',
                    borderRadius: 'var(--radius-full)',
                    overflow: 'hidden',
                    marginBottom: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    {Object.entries(budgetData.stagePercentages).map(([stage, percentage]) => (
                        percentage > 0 && (
                            <div
                                key={stage}
                                style={{
                                    width: `${percentage}%`,
                                    background: stageColors[stage],
                                    transition: 'width 0.3s ease'
                                }}
                                title={`${stage}: ${percentage}%`}
                            />
                        )
                    ))}
                </div>

                {/* Stage Details */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)'
                }}>
                    {Object.entries(FUNNEL_STAGES).map(([key, stage]) => {
                        const amount = budgetData.byStage[key] || 0;
                        const percentage = budgetData.stagePercentages[key] || 0;

                        return (
                            <div key={key} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)'
                                }}>
                                    <span style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '2px',
                                        background: stageColors[key]
                                    }} />
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500
                                        }}>
                                            {key}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {stage.label}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600
                                    }}>
                                        {formatBudget(amount)}
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        {percentage}%
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Warnings */}
            {budgetData.warnings.length > 0 && (
                <div style={{
                    padding: 'var(--space-3)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'rgba(var(--color-warning-rgb), 0.1)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-2)'
                    }}>
                        <AlertTriangle
                            size={14}
                            style={{
                                color: 'var(--color-warning)',
                                flexShrink: 0,
                                marginTop: '2px'
                            }}
                        />
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {budgetData.warnings.map((warning, idx) => (
                                <div key={idx} style={{ marginBottom: idx < budgetData.warnings.length - 1 ? 'var(--space-2)' : 0 }}>
                                    {warning}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Platform Indicator */}
            <div style={{
                padding: 'var(--space-3)',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)'
            }}>
                <span>Showing: {activePlatform.toUpperCase()}</span>
                <TrendingUp size={14} />
            </div>
        </>
    );

    // When embedded, just return the content without container
    if (embedded) {
        return (
            <div style={{ borderTop: '1px solid var(--color-border)' }}>
                {/* Header for embedded mode */}
                <div style={{
                    padding: 'var(--space-4)',
                    paddingBottom: 0
                }}>
                    <h4 style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--color-text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <PieChart size={12} />
                        Budget Allocation
                    </h4>
                </div>
                {content}
            </div>
        );
    }

    // Standalone mode with full container
    return (
        <div style={{
            width: '280px',
            background: 'var(--color-surface)',
            borderLeft: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <PieChart size={14} />
                    Budget Overview
                </h3>
            </div>
            {content}
        </div>
    );
};

export default BudgetOverview;
